import { useMemo, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { TermsConditionsDrawer } from '../../../../components/terms-conditions-privacy-policy';
import { Header, Text } from '../../../../ui-components';
import { Accordion, AccordionItem } from '../../../../ui-components/accordion';
import { getFAQItems } from '../../utils/faq';
import { CheckBalanceModalLink, GiftCardLink } from './GiftCardsFAQSectionUI';

export type FAQSchemaItem = {
    id: string;
    title: string;
    answer: string;
};

export type FAQItem = {
    id: string;
    title: string;
    answer: React.ReactNode;
    answerPlainText?: string;
};

type GiftCardsFAQSectionProps = {
    onCheckBalanceClick: () => void;
    faqSectionRef?: React.RefObject<HTMLDivElement>;
};

export const GiftCardsFAQSection = ({
    onCheckBalanceClick,
    faqSectionRef,
}: GiftCardsFAQSectionProps): React.ReactElement => {
    const [isTermsConditionsOpen, setIsTermsConditionsOpen] = useState(false);
    const intl = useIntl();

    const faqItems = useMemo(
        () => getFAQItems(intl, onCheckBalanceClick),
        [intl, onCheckBalanceClick],
    );

    return (
        <Flex
            as="section"
            flexDirection={{ base: 'column', md: 'row' }}
            gap={8}
            px={{ base: 4, sm: 8, md: 0 }}
            data-testid="gift-cards-faq-section"
            ref={faqSectionRef}
        >
            <Flex flexDirection="column" gap={4} width={{ base: 'full', md: '50%' }}>
                <Header as="h4" variant="header4" style="bold">
                    <FormattedMessage
                        id="gift_cards.landing_page.faq.title"
                        defaultMessage="Frequently Asked Questions"
                    />
                </Header>
                <Text>
                    <FormattedMessage
                        id="gift_cards.landing_page.faq.description"
                        defaultMessage="If you have more questions, please visit our full <terms>Terms and Conditions</terms> and <faqLink>FAQ</faqLink> page, or contact our <contactSupport>customer support</contactSupport> team."
                        values={{
                            terms: (chunks: React.ReactNode[]): React.ReactElement =>
                                CheckBalanceModalLink(chunks, {
                                    onClick: () => setIsTermsConditionsOpen(true),
                                    'data-testid': 'faq-terms-conditions-button',
                                }),
                            faqLink: (chunks: React.ReactNode[]): React.ReactElement =>
                                GiftCardLink(chunks, { href: '/faqs.html' }),
                            contactSupport: (chunks: React.ReactNode[]): React.ReactElement =>
                                GiftCardLink(chunks, { href: '/contactus' }),
                        }}
                    />
                </Text>
            </Flex>
            <Box width={{ base: 'full', md: '50%' }}>
                <Accordion id="gift-cards-faq-accordion" defaultIndex={0}>
                    {faqItems.map((item) => (
                        <AccordionItem
                            key={item.title}
                            title={item.title}
                            id={item.id}
                            titleTag="h4"
                            data-testid={`accordion-item-${item.id}`}
                        >
                            {typeof item.answer === 'string' ? (
                                <Text>{item.answer}</Text>
                            ) : (
                                item.answer
                            )}
                        </AccordionItem>
                    ))}
                </Accordion>
            </Box>
            <TermsConditionsDrawer
                placement="right"
                isOpen={isTermsConditionsOpen}
                onClose={(): void => setIsTermsConditionsOpen(false)}
                prefix="gift-cards-faq"
            />
        </Flex>
    );
};

GiftCardsFAQSection.defaultProps = {
    faqSectionRef: null,
};
